import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoService from "@saccoService";

const QUERY_KEY = 'sacco_deposits';

/**
 * Custom hook to fetch sacco deposits for a specific membership ID.
 *
 * @param {string} membershipId - The unique identifier for the membership whose deposits need to be fetched.
 * @return {object} Returns a query object containing the state and data of the request,
 *                  including loading status, error information, and the fetched deposits.
 */
export function useFetchSaccoDeposits(membershipId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEY, membershipId],
    queryFn: async () => {
      return await saccoService.fetchDeposits(user.id, membershipId)
    },
    enabled: !!user && !!membershipId,
  });
}

/**
 * Custom hook to create a deposit for a membership.
 * @deprecated Use useCreateSaccoDepositWithDebit hook instead
 * @param {string} membershipId - The ID of the membership for which the deposit is being created.
 * @return {object} Returns a mutation object that includes methods and state for managing the deposit creation process.
 */
export function useCreateDeposit(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: SaccoDepositRequestDto) => {
      return await saccoService.createDeposit({
        ...input,
        userId: user!.id
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * A custom hook to update a deposit and automatically invalidate related queries for the given membership ID.
 *
 * @param {string} membershipId - The unique identifier for the membership associated with the deposit.
 * @return {object} An object returned by `useMutation` containing mutation functions and state.
 */
export function useUpdateSaccoDeposit(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (update: SaccoDepositUpdateRequest) => {
      return await saccoService.updateDeposit(update);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * Custom hook to handle the deletion of a deposit using a mutation.
 *
 * @param {string} membershipId - The ID of the membership associated with the deposit.
 * @return {Object} Returns a mutation object that provides methods and properties to manage the delete deposit mutation process.
 */
export function useDeleteSaccoDeposit(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      return await saccoService.deleteDeposit(id, user!.id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
